/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import { getFilterElementsDefault, useWorkplaceState } from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addQuestionaryItem,
    changeQuestionaryItem,
    deleteQuestionaryItem,
    getQuestionary,
    getQuestionaryExcel,
    getQuestionaryItem,
} from 'axios/questionaryApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    QuestionaryInfo,
    QuestionaryRequestFilter,
    QuestionaryTable,
} from 'models/api/questionary';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { deleteActionBtnFormatter } from 'utils/columnFormatters';
import QuestionaryModal from './Modal/QuestionaryModal';
import QuestionaryAddButton from './QuestionaryAddButton';
import QuestionaryFilter from './QuestionaryFilter';

const Questionary = () => {
    const { t } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { hasRole } = useAuth();

    return (
        <BOWorkplace<QuestionaryTable, QuestionaryInfo, QuestionaryRequestFilter>
            testId="questionary"
            pageTitle="BackOfficeClientQuestionary"
            filterParams={{
                id: '',
                name: '',
                tradeOrg: '',
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey={'BackOfficeId'} />,
                },
                { key: 'name', name: <Trans i18nKey={'BackOfficeName'} /> },
                { key: 'tradeOrgs', name: <Trans i18nKey={'BackOfficeTradeOrgShort'} /> },
                { key: 'dsc', name: <Trans i18nKey={'BackOfficeDescription'} /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('questionaryDelete')),
                },
            ]}
            storagePath="Questionary/columns"
            getFilterElements={getFilterElementsDefault([
                { name: t('BackOfficeId'), id: 'id' },
                { name: t('BackOfficeName'), id: 'name' },
                { name: t('BackOfficeTradeOrgShort'), id: 'tradeOrg' },
            ])}
            showModal={() => setIsModalVisible(true)}
            filter={<QuestionaryFilter />}
            modal={{
                component: isModalVisible && (
                    <QuestionaryModal
                        isOpen={isModalVisible}
                        toggle={() => setIsModalVisible(false)}
                    />
                ),
                addMutationFn: (item) => addQuestionaryItem(api, item),
                changeMutationFn: (item) => changeQuestionaryItem(api, item),
                fetchCurrentItemQueryFn: (item) => getQuestionaryItem(api, item),
            }}
            fetchQueryFn={(filter) => getQuestionary(api, filter)}
            buttons={
                hasRole('questionaryCreate')
                    ? [<QuestionaryAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            deleteMutationFn={(id) => deleteQuestionaryItem(api, id)}
            fetchExcelQueryFn={(filter) => getQuestionaryExcel(api, filter)}
            queryKey={['Questionary']}
            queryClient={queryClient}
        />
    );
};

const useQuestionaryWorkplaceState = () => useWorkplaceState<QuestionaryTable, QuestionaryInfo>();

export default Questionary;
export { useQuestionaryWorkplaceState };
